import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  styled,
  Chip,
  Grid,
} from "@mui/material";
import CInput from "../../../../components/CInput";
import CAutocomplete from "../../../../components/CAutocomplete";
import CDatePicker from "../../../../components/CDatePicker";
import CEndDatePicker from "../../../../components/CEndDatePicker";
import Cselect from "../../../../components/CSelect";
import {
  createScope,
  updateScope,
  getProjectRoomBoqs,
  getScopeDetails,
} from "../../../../services/scopeService";
import {
  getProjectScopes,
  getActivityDependencies,
  createActivity,
} from "../../../../services/scheduleService";
import { IoArrowBack } from "react-icons/io5";
import moment from "moment";
import { ReactComponent as WhiteCrossIcon } from "../../../../assets/svg/WhiteCrossIcon.svg";

const StyledButton = styled(Button)(({ theme }) => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "1px solid #4D6BF6",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: "0",
  "&:hover": {
    borderColor: "#4D6BF6",
    color: "#ffffff",
    backgroundColor: "#4D6BF6",
  },
  "&:disabled": {
    opacity: 0.7,
    borderColor: "#4D6BF6",
    color: "#ffffff",
    backgroundColor: "#4D6BF6",
    cursor: "not-allowed",
  },
}));

const GreyStyledButton = styled(Button)(({ theme }) => ({
  background: "#fff",
  color: "#0D0D0E",
  boxShadow: "none",
  border: "1px solid #D7D8D9",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: "0",
}));

const AddActivityModal = ({
  open,
  onClose,
  onSave,
  editingScope,
  projectId,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "",
    scopePackage: null,
    dependencies: [],
    startDate: null,
    endDate: null,
    effort: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [roomBoqsData, setRoomBoqsData] = useState(null);
  const [scopeDetailsData, setScopeDetailsData] = useState(null);
  const [projectScopes, setProjectScopes] = useState([]);
  const [activityDependencies, setActivityDependencies] = useState([]);
  const [step, setStep] = useState(1); // 1: Details, 2: Select Items

  const [selectedItems, setSelectedItems] = useState({
    boqs: new Set(),
  });

  // Activity type options
  const activityTypes = [
    { label: "Design", value: "Design" },
    { label: "Installation", value: "Installation" },
    { label: "Testing", value: "Testing" },
    { label: "Inspection", value: "Inspection" },
    { label: "Delivery", value: "Delivery" },
  ];

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, projectId, editingScope]);

  const loadData = async () => {
    try {
      setLoadingData(true);

      const [
        roomBoqsResponse,
        scopeDetailsResponse,
        scopesResponse,
      ] = await Promise.all([
        getProjectRoomBoqs(projectId),
        editingScope
          ? getScopeDetails(editingScope.scope_id)
          : Promise.resolve({ success: true, rooms: [] }),
        getProjectScopes(projectId),
      ]);

      if (roomBoqsResponse.success) {
        setRoomBoqsData(roomBoqsResponse);
      }

      if (scopeDetailsResponse.success) {
        setScopeDetailsData(scopeDetailsResponse);
      }

      if (scopesResponse.success) {
        console.log("Scopes Response:", scopesResponse);
        setProjectScopes(scopesResponse.scopes || []);
      }

      // Reset activity dependencies - will be fetched when scope is selected
      setActivityDependencies([]);

      // Initialize form data
      if (editingScope) {
        setFormData({
          name: editingScope.name || "",
          description: editingScope.description || "",
        });

        // Pre-select items from scope details
        if (scopeDetailsResponse?.rooms && roomBoqsResponse?.rooms) {
          const selectedRooms = new Set();
          const selectedInclusions = new Set();
          const selectedBoqs = new Set();

          scopeDetailsResponse.rooms.forEach((scopeRoom) => {
            const fullRoom = roomBoqsResponse.rooms.find(
              (r) => r.room_id === scopeRoom.room_id
            );
            if (!fullRoom) return;

            const allInclusionsSelected = fullRoom.inclusions?.every(
              (fullInclusion) => {
                const scopeInclusion = scopeRoom.inclusions?.find(
                  (si) => si.id === fullInclusion.id
                );
                if (!scopeInclusion) return false;

                return fullInclusion.boqs?.every((fullBoq) =>
                  scopeInclusion.boqs?.some((sb) => sb.id === fullBoq.id)
                );
              }
            );

            if (allInclusionsSelected) {
              selectedRooms.add(scopeRoom.room_id);
              scopeRoom.inclusions?.forEach((scopeInclusion) => {
                selectedInclusions.add(scopeInclusion.id);
                scopeInclusion.boqs?.forEach((scopeBoq) => {
                  selectedBoqs.add(scopeBoq.id);
                });
              });
            } else {
              scopeRoom.inclusions?.forEach((scopeInclusion) => {
                const fullInclusion = fullRoom.inclusions?.find(
                  (fi) => fi.id === scopeInclusion.id
                );
                if (!fullInclusion) return;

                const allBoqsSelected = fullInclusion.boqs?.every((fullBoq) =>
                  scopeInclusion.boqs?.some((sb) => sb.id === fullBoq.id)
                );

                if (allBoqsSelected) {
                  selectedInclusions.add(scopeInclusion.id);
                  scopeInclusion.boqs?.forEach((scopeBoq) => {
                    selectedBoqs.add(scopeBoq.id);
                  });
                } else {
                  scopeInclusion.boqs?.forEach((scopeBoq) => {
                    selectedBoqs.add(scopeBoq.id);
                  });
                }
              });
            }
          });

          setSelectedItems({
            boqs: selectedBoqs,
          });
        }
      } else {
        setFormData({
          name: "",
          description: "",
          type: "",
          scopePackage: null,
          dependencies: [],
          startDate: null,
          endDate: null,
          effort: "",
        });
        setSelectedItems({
          boqs: new Set(),
        });
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data. Please try again.");
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });

    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      });
    }
  };

  // Fetch dependencies when scope is selected
  const handleScopeChange = async (newValue) => {
    handleInputChange("scopePackage", newValue);

    // Reset dependencies and date fields when scope changes
    setFormData((prev) => ({
      ...prev,
      scopePackage: newValue,
      dependencies: [],
      startDate: null,
      endDate: null,
      effort: "",
    }));
    setActivityDependencies([]);

    if (newValue && newValue.scope_id) {
      try {
        const dependenciesResponse = await getActivityDependencies(projectId, newValue.scope_id);
        if (dependenciesResponse.success) {
          setActivityDependencies(dependenciesResponse.data || []);
        }
      } catch (error) {
        console.error("Error fetching activity dependencies:", error);
        toast.error("Failed to load dependencies. Please try again.");
      }
    }
  };

  // Check if scope is selected
  const isScopeSelected = () => {
    return formData.scopePackage && formData.scopePackage.scope_id;
  };

  // Helper function to transform date format
  const transformDateFormat = (date) => {
    if (!date) return null;
    return moment(date).format("YYYY-MM-DD");
  };

  // Calculate effort based on start and end dates
  const calculateEffort = (startDate, endDate) => {
    if (!startDate || !endDate) return "";
    const start = moment(startDate);
    const end = moment(endDate);
    const diffInDays = end.diff(start, "days") + 1; // +1 to include both start and end days
    return diffInDays.toString();
  };

  // Calculate end date based on start date and effort
  const calculateEndDate = (startDate, effort) => {
    if (!startDate || !effort || effort <= 0) return null;
    const start = moment(startDate);
    const end = start.add(parseInt(effort) - 1, "days"); // -1 because effort includes start day
    return end.format("YYYY-MM-DD");
  };

  // Get the latest end date from selected dependencies
  const getLatestDependencyEndDate = (dependencyNames) => {
    if (!dependencyNames || dependencyNames.length === 0) return null;

    const filteredDeps = dependencyNames.filter((dep) => dep !== "None");
    if (filteredDeps.length === 0) return null;

    let latestEndDate = null;
    filteredDeps.forEach((depName) => {
      const dependency = activityDependencies.find(
        (d) => d.milestone_name === depName
      );
      if (dependency && dependency.end_date) {
        const endDate = moment(dependency.end_date);
        if (!latestEndDate || endDate.isAfter(latestEndDate)) {
          latestEndDate = endDate;
        }
      }
    });

    return latestEndDate ? latestEndDate.add(1, "days").format("YYYY-MM-DD") : null;
  };

  // Check if dependency is "None" or no dependency selected
  const isNoDependency = () => {
    return (
      !formData.dependencies ||
      formData.dependencies.length === 0 ||
      (formData.dependencies.length === 1 && formData.dependencies[0] === "None")
    );
  };

  // Check if a real dependency is selected (not "None")
  const hasRealDependency = () => {
    return (
      formData.dependencies &&
      formData.dependencies.length > 0 &&
      !formData.dependencies.includes("None")
    );
  };

  const handleDependenciesChange = (selectedDependencies) => {
    let updatedDependencies = [];

    if (Array.isArray(selectedDependencies)) {
      // Remove "None" from previous selections if user is adding real dependencies
      const prevWithoutNone = (formData.dependencies || []).filter(
        (d) => d !== "None"
      );
      updatedDependencies = [...prevWithoutNone, ...selectedDependencies];
    } else {
      // single selection
      const prevWithoutNone = (formData.dependencies || []).filter(
        (d) => d !== "None"
      );
      updatedDependencies = [...prevWithoutNone, selectedDependencies];
    }

    console.log("Selected Dependencies:", updatedDependencies);

    // Case 1: If "None" selected with others → keep only "None"
    if (
      updatedDependencies.includes("None") &&
      updatedDependencies.length > 1
    ) {
      updatedDependencies = ["None"];
    }

    // Case 2: Only "None" selected or empty
    if (
      updatedDependencies.length === 0 ||
      (updatedDependencies.length === 1 && updatedDependencies[0] === "None")
    ) {
      setFormData((prev) => ({
        ...prev,
        dependencies: updatedDependencies.length === 0 ? [] : ["None"],
        startDate: null,
        endDate: null,
        effort: "",
      }));
    }
    // Case 3: Real dependencies selected (no "None")
    else if (
      updatedDependencies.length > 0 &&
      !updatedDependencies.includes("None")
    ) {
      // Calculate start date from latest dependency end date + 1 day
      const latestStartDate = getLatestDependencyEndDate(updatedDependencies);

      setFormData((prev) => ({
        ...prev,
        dependencies: updatedDependencies,
        startDate: latestStartDate,
        endDate: null,
        effort: "",
      }));
    }

    // Clear validation errors
    if (errors.dependencies) {
      setErrors((prev) => ({
        ...prev,
        dependencies: "",
      }));
    }
  };

  const handleDeleteDependencies = (itemToRemove) => {
    console.log("Removing dependency:", itemToRemove);

    // Remove the clicked item
    let newDependencies = formData.dependencies.filter(
      (dep) => dep !== itemToRemove
    );

    // Handle "None" logic
    if (newDependencies.includes("None") && newDependencies.length > 1) {
      newDependencies = ["None"];
    }

    // Update formData based on newDependencies
    if (
      newDependencies.length === 0 ||
      (newDependencies.length === 1 && newDependencies[0] === "None")
    ) {
      setFormData((prev) => ({
        ...prev,
        dependencies: newDependencies.length === 0 ? [] : ["None"],
        startDate: null,
        endDate: null,
        effort: "",
      }));
    } else if (newDependencies.length > 0) {
      // Recalculate start date from remaining dependencies
      const latestStartDate = getLatestDependencyEndDate(newDependencies);

      setFormData((prev) => ({
        ...prev,
        dependencies: newDependencies,
        startDate: latestStartDate,
        endDate: null,
        effort: "",
      }));
    }

    // Clear validation errors if any
    if (errors.dependencies) {
      setErrors((prev) => ({
        ...prev,
        dependencies: "",
      }));
    }

    console.log("New dependencies:", newDependencies);
  };

  // Handle start date change (only enabled for "None" or no dependency)
  const handleStartDateChange = (date) => {
    const formattedDate = transformDateFormat(date);

    // If end date exists, recalculate effort
    if (formData.endDate) {
      const effort = calculateEffort(formattedDate, formData.endDate);
      setFormData({
        ...formData,
        startDate: formattedDate,
        effort: effort,
      });
    } else if (formData.effort) {
      // If effort exists, recalculate end date
      const endDate = calculateEndDate(formattedDate, formData.effort);
      setFormData({
        ...formData,
        startDate: formattedDate,
        endDate: endDate,
      });
    } else {
      setFormData({
        ...formData,
        startDate: formattedDate,
      });
    }

    if (errors.startDate) {
      setErrors({
        ...errors,
        startDate: "",
      });
    }
  };

  // Handle end date change (only enabled for "None" or no dependency)
  const handleEndDateChange = (date) => {
    const formattedDate = transformDateFormat(date);
    const effort = calculateEffort(formData.startDate, formattedDate);

    setFormData({
      ...formData,
      endDate: formattedDate,
      effort: effort,
    });

    if (errors.endDate) {
      setErrors({
        ...errors,
        endDate: "",
      });
    }
  };

  // Handle effort change
  const handleEffortChange = (value) => {
    const effortValue = value;

    // Case 1: No dependency or "None" - calculate end date if start date exists and no end date
    if (isNoDependency()) {
      if (formData.startDate && !formData.endDate && effortValue) {
        const endDate = calculateEndDate(formData.startDate, effortValue);
        setFormData({
          ...formData,
          effort: effortValue,
          endDate: endDate,
        });
      } else {
        setFormData({
          ...formData,
          effort: effortValue,
        });
      }
    }
    // Case 2: Real dependency selected - always calculate end date from start date + effort
    else if (hasRealDependency()) {
      const endDate = calculateEndDate(formData.startDate, effortValue);
      setFormData({
        ...formData,
        effort: effortValue,
        endDate: endDate,
      });
    }

    if (errors.effort) {
      setErrors({
        ...errors,
        effort: "",
      });
    }
  };

  const handleBoqToggle = (boqId, inclusionId, roomId) => {
    setSelectedItems((prev) => {
      const newSelected = { ...prev };

      if (newSelected.boqs.has(boqId)) {
        newSelected.boqs.delete(boqId);
      } else {
        newSelected.boqs.add(boqId);
      }

      return newSelected;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Activity name is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.length > 1000) {
      newErrors.description = "Description cannot exceed 1000 characters";
    }

    if (!formData.type) {
      newErrors.type = "Activity type is required";
    }

    if (!formData.scopePackage) {
      newErrors.scopePackage = "Scope package is required";
    }

    if (!formData.dependencies || formData.dependencies.length === 0) {
      newErrors.dependencies = "Dependencies are required";
    }

    // Start date is always required
    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    // Either End Date or Efforts must be entered
    if (!formData.endDate && (!formData.effort || formData.effort <= 0)) {
      newErrors.endDate = "Either end date or effort is required";
      newErrors.effort = "Either end date or effort is required";
    }

    // Validate end date is after start date if both exist
    if (formData.startDate && formData.endDate) {
      const startDate = moment(formData.startDate);
      const endDate = moment(formData.endDate);
      if (endDate.isBefore(startDate)) {
        newErrors.endDate = "End date must be after start date";
      }
    }

    const boqIds = Array.from(selectedItems.boqs);
    if (boqIds.length === 0) {
      newErrors.items = "Please select at least one BOQ item";
    }

    setErrors(newErrors);

    // Show toast errors for validation failures
    if (Object.keys(newErrors).length > 0) {
      const errorMessages = Object.values(newErrors);
      toast.error(errorMessages[0]); // Show first error as toast
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);

      // Get BOQ IDs from selected items
      const boqIds = Array.from(selectedItems.boqs);

      // Get dependency milestone IDs (exclude "None")
      // Build dependency IDs
      let dependencyIds = [];

      if (
        formData.dependencies.length > 0 &&
        !(
          formData.dependencies.length === 1 &&
          formData.dependencies[0] === "None"
        )
      ) {
        dependencyIds = formData.dependencies
          .filter((dep) => dep !== "None")
          .map((dep) => {
            const dependency = activityDependencies.find(
              (d) => d.milestone_name === dep
            );
            return dependency ? dependency.milestone_id : null;
          })
          .filter((id) => id !== null);
      }

      const payload = {
        name: formData.name.trim(),
        type: formData.type,
        description: formData.description.trim(),
        boq_ids: boqIds,
        dependencies: dependencyIds,
        start_date: formData.startDate,
      };

      // Add end date if available
      if (formData.endDate) {
        payload.end_date = formData.endDate;
      }

      // Add effort if available
      if (formData.effort) {
        payload.effort = parseInt(formData.effort);
      }

      console.log("Payload for creating activity:", payload);
      await createActivity(formData.scopePackage.id, payload);
      toast.success("Activity created successfully!");
      onSave();
    } catch (error) {
      console.error("Error creating activity:", error);
      toast.error("Failed to add activity. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      type: "",
      scopePackage: null,
      dependencies: [],
      startDate: null,
      endDate: null,
      effort: "",
    });
    setSelectedItems({
      boqs: new Set(),
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getTotalSelectedCount = () => {
    return selectedItems.boqs.size;
  };

  const handleNext = () => {
    // Validate Step 1 fields before proceeding
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Activity name is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.length > 1000) {
      newErrors.description = "Description cannot exceed 1000 characters";
    }

    if (!formData.type) {
      newErrors.type = "Activity type is required";
    }

    if (!formData.scopePackage) {
      newErrors.scopePackage = "Scope package is required";
    }

    if (!formData.dependencies || formData.dependencies.length === 0) {
      newErrors.dependencies = "Dependencies are required";
    }

    // Start date is always required
    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    // Either End Date or Efforts must be entered
    if (!formData.endDate && (!formData.effort || formData.effort <= 0)) {
      newErrors.endDate = "Either end date or effort is required";
      newErrors.effort = "Either end date or effort is required";
    }

    // Validate end date is after start date if both exist
    if (formData.startDate && formData.endDate) {
      const startDate = moment(formData.startDate);
      const endDate = moment(formData.endDate);
      if (endDate.isBefore(startDate)) {
        newErrors.endDate = "End date must be after start date";
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const errorMessages = Object.values(newErrors);
      toast.error(errorMessages[0]); // Show first error as toast
      return;
    }

    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          maxHeight: "90vh",
          overflowX: "clip",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          fontSize: "24px",
          fontWeight: 600,
          color: "#0D0D0E",
          paddingBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            fontSize: "16px",
            fontWeight: 600,
            letterSpacing: 0,
            display: "flex",
            alignItems: "center",
            gap: 5,
            cursor: step === 2 ? "pointer" : "default",
          }}
          onClick={() => step === 2 && setStep(1)}
        >
          {step === 2 && (
            <IoArrowBack
              sx={{ fontSize: "14px", cursor: "pointer" }}
              onClick={handleBack}
            />
          )}
          Add  New Activity
        </div>
        <div style={{ fontSize: "14px", fontWeight: 600, letterSpacing: 0 }}>
          Step {step} of 2
        </div>
      </DialogTitle>
      <DialogContent sx={{ padding: "16px" }}>
        {loadingData ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {step === 1 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Row 1: Scope Package (full width) */}
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <CAutocomplete
                    label="Scope Package"
                    placeholder="Select scope package"
                    value={formData.scopePackage}
                    onChange={(e, newValue) => {
                      handleScopeChange(newValue);
                    }}
                    options={projectScopes}
                    getOptionLabel={(option) => option.name}
                    error={!!errors.scopePackage}
                    helpertext={errors.scopePackage}
                    required
                  />
                </div>

                {/* Row 2: Activity Name | Activity Type (2 columns) */}
                <div
                  style={{ display: "flex", flexDirection: "row", gap: "20px" }}
                >
                  <CInput
                    label="Activity Name"
                    placeholder="Enter activity name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    error={!!errors.name}
                    helpertext={errors.name}
                    required
                  />

                  <CAutocomplete
                    label="Activity Type"
                    placeholder="Select activity type"
                    value={activityTypes.find(
                      (type) => type.value === formData.type
                    )}
                    onChange={(e, newValue) => {
                      handleInputChange("type", newValue?.value || "");
                    }}
                    options={activityTypes}
                    getOptionLabel={(option) => option.label}
                    error={!!errors.type}
                    helpertext={errors.type}
                    required
                  />
                </div>

                {/* Row 3: Dependencies (full width) */}
                <div>
                  <Cselect
                    renderTags={
                      isScopeSelected()
                        ? [
                          { project_name: "None" },
                          ...activityDependencies.map((dep) => ({
                            project_name: dep.milestone_name,
                          })),
                        ]
                        : []
                    }
                    multiple
                    placeholder={isScopeSelected() ? "Select Dependencies" : "Select scope package first"}
                    handleSelect={(e) => handleDependenciesChange(e)}
                    error={!!errors.dependencies}
                    helpertext={errors.dependencies}
                    noMarginTop
                    disabled={!isScopeSelected()}
                  />
                  {formData.dependencies &&
                    formData.dependencies.length > 0 && (
                      <Box
                        sx={{
                          marginTop: 2,
                          marginBottom: 2,
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {formData.dependencies.map((item) => (
                          <Chip
                            key={item}
                            label={item}
                            onDelete={() => handleDeleteDependencies(item)}
                            deleteIcon={<WhiteCrossIcon />}
                            sx={{
                              backgroundColor: "#0D0D0D",
                              color: "#fff",
                              fontSize: "12px",
                              fontWeight: "500",
                              borderRadius: "10px",
                              padding: "1vh .5vw",
                              marginTop: "0 !important",
                            }}
                          />
                        ))}
                      </Box>
                    )}
                </div>

                {/* Row 4: Start Date | End Date | Efforts (3 columns) */}
                <div
                  style={{ display: "flex", flexDirection: "row", gap: "20px" }}
                >
                  <CDatePicker
                    label="Start Date"
                    value={
                      formData.startDate ? new Date(formData.startDate) : null
                    }
                    onChange={handleStartDateChange}
                    error={!!errors.startDate}
                    helperText={errors.startDate}
                    disabled={!isScopeSelected() || hasRealDependency()}
                    disablePast
                    required
                  />
                  <CEndDatePicker
                    label="End Date"
                    value={formData.endDate}
                    minDate={
                      formData.startDate
                        ? new Date(formData.startDate)
                        : new Date()
                    }
                    onChange={handleEndDateChange}
                    error={!!errors.endDate}
                    helperText={errors.endDate}
                    disabled={!isScopeSelected() || hasRealDependency()}
                  />
                  <CInput
                    label="Efforts (in days)"
                    placeholder={"Enter effort in days"}
                    value={formData.effort}
                    onChange={(e) => handleEffortChange(e.target.value)}
                    error={!!errors.effort}
                    helpertext={errors.effort}
                    disabled={!isScopeSelected() || (isNoDependency() && formData.endDate && formData.startDate)}
                    type="number"
                    inputProps={{ min: 1 }}
                  />
                </div>

                {/* Row 5: Description textarea (full width) */}
                <CInput
                  label="Description"
                  multiline
                  rows={4}
                  placeholder="Brief description about this activity"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  error={!!errors.description}
                  helpertext={errors.description}
                  required
                />
              </Box>
            ) : (
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                    mb: 2,
                    letterSpacing: 0,
                  }}
                >
                  Select BOQ Items ({getTotalSelectedCount()} selected)
                </Typography>

                {errors.items && (
                  <Typography color="error" sx={{ fontSize: "12px", mb: 2 }}>
                    {errors.items}
                  </Typography>
                )}

                {roomBoqsData?.rooms?.map((room) => (
                  <Box
                    key={room.room_id}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "16px",
                      marginBottom: "16px",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        mb: 2,
                        color: "#0D0D0E",
                      }}
                    >
                      🏠 {room.room_name}
                    </Typography>

                    {room.inclusions?.map((inclusion) => (
                      <Box
                        key={inclusion.id}
                        sx={{
                          marginBottom: "16px",
                          padding: "12px",
                          border: "1px solid #f0f0f0",
                          borderRadius: "6px",
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            mb: 2,
                            color: "#0D0D0E",
                          }}
                        >
                          🔧 {inclusion.name}
                        </Typography>

                        {inclusion.boqs?.map((boq) => (
                          <Box
                            key={boq.id}
                            sx={{
                              padding: "8px 12px",
                              border: "1px solid #e8e8e8",
                              borderRadius: "4px",
                              backgroundColor: "#fff",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Checkbox
                                checked={selectedItems.boqs.has(boq.id)}
                                onChange={() =>
                                  handleBoqToggle(
                                    boq.id,
                                    inclusion.id,
                                    room.room_id
                                  )
                                }
                                sx={{ padding: 0, transform: "scale(0.9)" }}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, mb: 0.5 }}
                                >
                                  {boq.item_name}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      padding: "2px 6px",
                                      backgroundColor: "#e3f2fd",
                                      borderRadius: "4px",
                                      color: "#1976d2",
                                    }}
                                  >
                                    {boq.type}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      padding: "2px 6px",
                                      backgroundColor: "#f3e5f5",
                                      borderRadius: "4px",
                                      color: "#7b1fa2",
                                    }}
                                  >
                                    {boq.type === "Activities"
                                      ? `${boq.days || "-"} days`
                                      : `${boq.quantity || "-"} ${boq.unit_of_measurement || ""}`}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ padding: "16px 24px", justifyContent: "space-between" }}
      >
        <GreyStyledButton onClick={handleClose}>Cancel</GreyStyledButton>

        <StyledButton
          onClick={step === 1 ? handleNext : handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              {step === 1 ? "Saving..." : "Creating..."}
            </>
          ) : (
            <>{step === 1 ? "Next" : "Add Activity"}</>
          )}
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddActivityModal;
